import { Chess } from "chess.js";
import { applyLegalMove, type EvaluationScore } from "@chessla/shared";

export interface TemporaryMoveGrade {
  status: "pending" | "ready" | "unavailable" | "failed";
  attemptedSan: string;
  bestMoveSan?: string | null;
  startingEvaluation?: EvaluationScore | null;
  evaluation: EvaluationScore | null;
  deltaCp: number | null;
  verdict: "holds" | "slips" | "loses_material" | "forces_mate" | "gets_mated" | null;
  consequence: string | null;
}

export type GradeTemporaryMove = (
  beforeFen: string,
  moveUci: string,
  signal: AbortSignal,
) => Promise<TemporaryMoveGrade>;

export interface TemporaryPositionEvaluation {
  score: EvaluationScore;
  bestMove: string | null;
}

export type EvaluateTemporaryPosition = (
  fen: string,
  signal: AbortSignal,
) => Promise<TemporaryPositionEvaluation | null>;

const emptyGrade = (status: TemporaryMoveGrade["status"], attemptedSan: string): TemporaryMoveGrade => ({
  status,
  attemptedSan,
  bestMoveSan: null,
  startingEvaluation: null,
  evaluation: null,
  deltaCp: null,
  verdict: null,
  consequence: null,
});

const exactScore = (score: EvaluationScore): EvaluationScore | null => {
  if (score.mate != null) return { cp: null, mate: score.mate };
  return score.cp != null && Math.abs(score.cp) < 10_000 ? { cp: score.cp, mate: null } : null;
};

const playerScoreAfterMove = (score: EvaluationScore): EvaluationScore => ({
  cp: score.cp == null ? null : -score.cp,
  mate: score.mate == null ? null : -score.mate,
});

const bestReplyCapture = (fen: string, uci: string | null): { san: string; piece: string } | null => {
  if (!uci) return null;
  try {
    const move = new Chess(fen).move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] });
    if (!move?.captured) return null;
    const piece = ({ p: "pawn", n: "knight", b: "bishop", r: "rook", q: "queen", k: "king" } as const)[move.captured];
    return piece ? { san: move.san, piece } : null;
  } catch {
    return null;
  }
};

export const createTemporaryMoveGrader =
  (evaluate: EvaluateTemporaryPosition): GradeTemporaryMove =>
  async (beforeFen, moveUci, signal) => {
    signal.throwIfAborted();
    const attempted = applyLegalMove(beforeFen, moveUci);
    if (!attempted) return emptyGrade("unavailable", moveUci);
    if (new Chess(attempted.fen).isCheckmate()) {
      return {
        status: "ready",
        attemptedSan: attempted.san,
        bestMoveSan: attempted.san,
        startingEvaluation: null,
        evaluation: { cp: null, mate: 1 },
        deltaCp: null,
        verdict: "forces_mate",
        consequence: `${attempted.san} is checkmate.`,
      };
    }

    try {
      const [beforeResult, afterResult] = await Promise.all([
        evaluate(beforeFen, signal),
        evaluate(attempted.fen, signal),
      ]);
      signal.throwIfAborted();
      const before = beforeResult && exactScore(beforeResult.score);
      const rawAfter = afterResult && exactScore(afterResult.score);
      if (!before || !rawAfter) return emptyGrade("unavailable", attempted.san);
      const bestMoveSan = beforeResult.bestMove
        ? (applyLegalMove(beforeFen, beforeResult.bestMove)?.san ?? null)
        : null;

      const evaluation = playerScoreAfterMove(rawAfter);
      if (evaluation.mate != null) {
        const forcesMate = evaluation.mate > 0;
        return {
          status: "ready",
          attemptedSan: attempted.san,
          bestMoveSan,
          startingEvaluation: before,
          evaluation,
          deltaCp: null,
          verdict: forcesMate ? "forces_mate" : "gets_mated",
          consequence: forcesMate
            ? `${attempted.san} leaves you with mate in ${Math.abs(evaluation.mate)}.`
            : `After ${attempted.san}, the opponent has mate in ${Math.abs(evaluation.mate)}.`,
        };
      }

      if (before.cp == null || evaluation.cp == null) return emptyGrade("unavailable", attempted.san);
      const deltaCp = evaluation.cp - before.cp;
      const capture = deltaCp <= -200 ? bestReplyCapture(attempted.fen, afterResult?.bestMove ?? null) : null;
      const verdict = deltaCp >= -50 ? "holds" : capture ? "loses_material" : "slips";
      const consequence = capture
        ? `${capture.san} takes your ${capture.piece}.`
        : verdict === "holds"
          ? `${attempted.san} stays within 0.5 pawns of the starting evaluation.`
          : `${attempted.san} gives up ${(Math.abs(deltaCp) / 100).toFixed(1)} pawns of objective value.`;
      return {
        status: "ready",
        attemptedSan: attempted.san,
        bestMoveSan,
        startingEvaluation: before,
        evaluation,
        deltaCp,
        verdict,
        consequence,
      };
    } catch {
      signal.throwIfAborted();
      return emptyGrade("failed", attempted.san);
    }
  };
